import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Alert
} from "reactstrap";
import Select from "react-select";
import { saveCustomer } from "../components/Api/GmsCustomer";
import { getBranches } from "../components/Api/GmsBranch";
import { getCountries } from "../components/Api/GmsCountry";
import Swal from "sweetalert2";

const QuickAddCustomer = ({ isOpen, toggle, onCustomerAdded }) => {
  const [form, setForm] = useState({
    customer_NameEn: "",
    customer_NameAr: "",
    customer_Email: "",
    customer_ContactNo: "",
    customer_AddressEn: "",
    customer_AddressAr: "",
    customer_Branch_Id: null,
    customer_CreditLimit: 0,
    customer_Active: true,
    customer_Type: 1, // Always Individual
    customer_IsInternational: false,
    customer_RepDetails: "",
    customer_Notes: "",
    customer_ID_Number: "", // New field for National ID
    customer_DOB: "", // New field for Date of Birth
    customer_Salutation: "", // New field for Salutation
    customer_TRN: "", // New field for TRN
    customer_Country_Id: null, // New field for Country ID
    customer_CreatedDate: new Date(),
    customer_CreatedBy: Number(localStorage.getItem("userId")) || 1,
    customer_ModifiedDate: new Date(),
    customer_ModifiedBy: Number(localStorage.getItem("userId")) || 1,
  });

  const [branches, setBranches] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      loadBranches();
      loadCountries();
      resetForm();
    }
  }, [isOpen]);

  const loadCountries = async () => {
    try {
      const countriesData = await getCountries();
      setCountries(countriesData || []);
    } catch (error) {
      console.error("Error loading countries:", error);
      setCountries([]);
    }
  };

  const loadBranches = async () => {
    try {
      const branchesData = await getBranches();
      const options = branchesData.map(branch => ({
        value: branch.branch_Id,
        label: branch.branch_NameEn
      }));
      setBranchOptions(options);
      setBranches(branchesData);
    } catch (error) {
      console.error("Error loading branches:", error);
    }
  };

  const resetForm = () => {
    // Get user's branch ID from localStorage if available
    const userBranchId = localStorage.getItem("userBranchId");
    
    setForm({
      customer_NameEn: "",
      customer_NameAr: "",
      customer_Email: "",
      customer_ContactNo: "",
      customer_AddressEn: "",
      customer_AddressAr: "",
      customer_Branch_Id: userBranchId ? Number(userBranchId) : null,
      customer_CreditLimit: 0,
      customer_Active: true,
      customer_Type: 1, // Always Individual
      customer_IsInternational: false,
      customer_RepDetails: "",
      customer_Notes: "",
      customer_ID_Number: "", // New field for National ID
      customer_DOB: "", // New field for Date of Birth
      customer_TRN: "", // New field for TRN
      customer_Country_Id: null, // New field for Country ID
      customer_CreatedDate: new Date(),
      customer_CreatedBy: Number(localStorage.getItem("userId")) || 1,
      customer_ModifiedDate: new Date(),
      customer_ModifiedBy: Number(localStorage.getItem("userId")) || 1,
    });
    setValidationErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setForm(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Clear validation error for this field
    setValidationErrors(prev => ({
      ...prev,
      [name]: ""
    }));
  };

  const handleSelectChange = (name, selectedOption) => {
    setForm(prev => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : null
    }));
    // Clear validation error for this field
    setValidationErrors(prev => ({
      ...prev,
      [name]: ""
    }));
  };

  const validateForm = () => {
    const errors = {};

    // Customer Name (English), Contact Number, and Country are mandatory
    if (!form.customer_NameEn || form.customer_NameEn.trim() === "") {
      errors.customer_NameEn = "Customer Name (English) is required.";
    }
    if (!form.customer_ContactNo || form.customer_ContactNo.trim() === "") {
      errors.customer_ContactNo = "Contact Number is required.";
    }
    if (!form.customer_Country_Id || form.customer_Country_Id === null) {
      errors.customer_Country_Id = "Country is required.";
    }

    // Email validation (if provided)
    if (form.customer_Email && form.customer_Email.trim() !== "") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.customer_Email.trim())) {
        errors.customer_Email = "Please enter a valid email address.";
      }
    }

    // Contact number validation (if provided)
    if (form.customer_ContactNo && form.customer_ContactNo.trim() !== "") {
      const phoneRegex = /^\d+$/;
      if (!phoneRegex.test(form.customer_ContactNo.trim())) {
        errors.customer_ContactNo = "Contact number can only contain numbers.";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      Swal.fire("Validation Error", "Please fill in all required fields correctly.", "warning");
      return;
    }

    try {
      setLoading(true);
      
      const customerData = {
        ...form,
        customer_DOB: form.customer_DOB === "" ? null : form.customer_DOB,
        customer_CreatedBy: Number(localStorage.getItem("userId")) || 1,
        customer_ModifiedBy: Number(localStorage.getItem("userId")) || 1,
      };

      const response = await saveCustomer(customerData);
      
      // Check for success: response exists and either has a valid id (> 0) or a success message
      if (response && (response.id !== undefined && response.id !== null) && response.message && response.message.toLowerCase().includes("success")) {
        Swal.fire("Success", "Customer added successfully!", "success");
        resetForm();
        toggle();
        if (onCustomerAdded) {
          console.log("🔍 QuickAddCustomer - Passing to onCustomerAdded:", response);
          onCustomerAdded(response);
        }
      } else {
        Swal.fire("Error", response?.message || "Failed to add customer", "error");
      }
    } catch (error) {
      console.error("Error saving customer:", error);
      Swal.fire("Error", "Failed to add customer", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleCancel} size="lg">
      <ModalHeader toggle={handleCancel}>
        Quick Add Customer
      </ModalHeader>
      <ModalBody>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={2}>
              <FormGroup className="mb-3">
                <Label for="customer_Salutation">Salutation</Label>
                <Select
                  id="customer_Salutation"
                  name="customer_Salutation"
                  options={[
                    { value: "", label: "Select" },
                    { value: "Mr.", label: "Mr." },
                    { value: "Ms.", label: "Ms." },
                    { value: "Mrs.", label: "Mrs." },
                    { value: "Dr.", label: "Dr." }
                  ]}
                  value={form.customer_Salutation ? { value: form.customer_Salutation, label: form.customer_Salutation } : null}
                  onChange={(option) => handleSelectChange('customer_Salutation', option)}
                  isClearable
                  isSearchable
                  placeholder="Select"
                  classNamePrefix="select2-selection"
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      backgroundColor: 'white',
                      borderColor: state.isFocused ? '#556ee6' : '#ced4da',
                      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
                      minHeight: '38px'
                    }),
                    menu: (provided) => ({
                      ...provided,
                      backgroundColor: 'white',
                      zIndex: 9999,
                      border: '1px solid #ced4da',
                      boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)'
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
                      color: state.isSelected ? 'white' : '#495057',
                      '&:hover': {
                        backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
                      },
                    }),
                    singleValue: (provided) => ({
                      ...provided,
                      color: '#495057'
                    }),
                    placeholder: (provided) => ({
                      ...provided,
                      color: '#6c757d'
                    })
                  }}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup className="mb-3">
                <Label for="customer_NameEn">
                  Customer Name (English) <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  id="customer_NameEn"
                  name="customer_NameEn"
                  value={form.customer_NameEn}
                  onChange={handleInputChange}
                  placeholder="Enter customer name in English"
                  className={validationErrors.customer_NameEn ? 'is-invalid' : ''}
                />
                {validationErrors.customer_NameEn && (
                  <div className="invalid-feedback d-block">
                    {validationErrors.customer_NameEn}
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-3">
                <Label for="customer_NameAr">
                  Customer Name (Arabic)
                </Label>
                <Input
                  type="text"
                  id="customer_NameAr"
                  name="customer_NameAr"
                  value={form.customer_NameAr}
                  onChange={handleInputChange}
                  placeholder="Enter customer name in Arabic"
                  className={validationErrors.customer_NameAr ? 'is-invalid' : ''}
                />
                {validationErrors.customer_NameAr && (
                  <div className="invalid-feedback d-block">
                    {validationErrors.customer_NameAr}
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup className="mb-3">
                <Label for="customer_Email">Email</Label>
                <Input
                  type="email"
                  id="customer_Email"
                  name="customer_Email"
                  value={form.customer_Email}
                  onChange={handleInputChange}
                  placeholder="Enter email address"
                  className={validationErrors.customer_Email ? 'is-invalid' : ''}
                />
                {validationErrors.customer_Email && (
                  <div className="invalid-feedback d-block">
                    {validationErrors.customer_Email}
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-3">
                <Label for="customer_ContactNo">
                  Contact Number <span className="text-danger">*</span>
                </Label>
                <Input
                  type="number"
                  id="customer_ContactNo"
                  name="customer_ContactNo"
                  value={form.customer_ContactNo}
                  onChange={handleInputChange}
                  placeholder="Enter contact number"
                  className={validationErrors.customer_ContactNo ? 'is-invalid' : ''}
                />
                {validationErrors.customer_ContactNo && (
                  <div className="invalid-feedback d-block">
                    {validationErrors.customer_ContactNo}
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup className="mb-3">
                <Label for="customer_TRN">TRN</Label>
                <Input
                  type="text"
                  id="customer_TRN"
                  name="customer_TRN"
                  value={form.customer_TRN}
                  onChange={handleInputChange}
                  placeholder="Enter TRN (Tax Registration Number)"
                  className={validationErrors.customer_TRN ? 'is-invalid' : ''}
                />
                {validationErrors.customer_TRN && (
                  <div className="invalid-feedback d-block">
                    {validationErrors.customer_TRN}
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup className="mb-3">
                <Label for="customer_ID_Number">National ID Number</Label>
                <Input
                  type="text"
                  id="customer_ID_Number"
                  name="customer_ID_Number"
                  value={form.customer_ID_Number}
                  onChange={handleInputChange}
                  placeholder="Enter national ID number"
                  className={validationErrors.customer_ID_Number ? 'is-invalid' : ''}
                />
                {validationErrors.customer_ID_Number && (
                  <div className="invalid-feedback d-block">
                    {validationErrors.customer_ID_Number}
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-3">
                <Label for="customer_DOB">Date of Birth</Label>
                <Input
                  type="date"
                  id="customer_DOB"
                  name="customer_DOB"
                  value={form.customer_DOB}
                  onChange={handleInputChange}
                  className={validationErrors.customer_DOB ? 'is-invalid' : ''}
                />
                {validationErrors.customer_DOB && (
                  <div className="invalid-feedback d-block">
                    {validationErrors.customer_DOB}
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup className="mb-3">
                <Label for="customer_AddressEn">
                  Address (English)
                </Label>
                <Input
                  type="text"
                  id="customer_AddressEn"
                  name="customer_AddressEn"
                  value={form.customer_AddressEn}
                  onChange={handleInputChange}
                  placeholder="Enter address in English"
                  className={validationErrors.customer_AddressEn ? 'is-invalid' : ''}
                />
                {validationErrors.customer_AddressEn && (
                  <div className="invalid-feedback d-block">
                    {validationErrors.customer_AddressEn}
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-3">
                <Label for="customer_AddressAr">
                  Address (Arabic)
                </Label>
                <Input
                  type="text"
                  id="customer_AddressAr"
                  name="customer_AddressAr"
                  value={form.customer_AddressAr}
                  onChange={handleInputChange}
                  placeholder="Enter address in Arabic"
                  className={validationErrors.customer_AddressAr ? 'is-invalid' : ''}
                />
                {validationErrors.customer_AddressAr && (
                  <div className="invalid-feedback d-block">
                    {validationErrors.customer_AddressAr}
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup className="mb-3">
                <Label for="customer_Country_Id">
                  Country <span className="text-danger">*</span>
                </Label>
                <Select
                  id="customer_Country_Id"
                  name="customer_Country_Id"
                  options={countries.map(country => ({
                    value: country.country_Id,
                    label: `${country.country_NameEn} - ${country.country_NameAr}`
                  }))}
                  value={countries.map(country => ({
                    value: country.country_Id,
                    label: `${country.country_NameEn} - ${country.country_NameAr}`
                  })).find(option => option.value === form.customer_Country_Id) || null}
                  onChange={(option) => handleSelectChange('customer_Country_Id', option)}
                  isClearable={false}
                  isSearchable
                  placeholder="Select Country"
                  className={validationErrors.customer_Country_Id ? 'is-invalid' : ''}
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      minHeight: '38px',
                      borderColor: state.isFocused ? '#556ee6' : '#ced4da',
                      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
                    }),
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 9999,
                      maxHeight: 200,
                    }),
                    menuList: (provided) => ({
                      ...provided,
                      maxHeight: 180,
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
                      color: state.isSelected ? 'white' : '#495057',
                      '&:hover': {
                        backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
                      },
                    }),
                  }}
                />
                {validationErrors.customer_Country_Id && (
                  <div className="invalid-feedback d-block">
                    {validationErrors.customer_Country_Id}
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-3">
                <Label for="customer_Branch_Id">
                  Branch
                </Label>
                <Select
                  id="customer_Branch_Id"
                  name="customer_Branch_Id"
                  options={branchOptions}
                  value={branchOptions.find(option => option.value === form.customer_Branch_Id) || null}
                  onChange={(option) => handleSelectChange('customer_Branch_Id', option)}
                  isClearable
                  isSearchable
                  placeholder="Select Branch"
                  className={validationErrors.customer_Branch_Id ? 'is-invalid' : ''}
                  isDisabled={!!localStorage.getItem("userBranchId")}
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      minHeight: '38px',
                      borderColor: state.isFocused ? '#556ee6' : '#ced4da',
                      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
                    }),
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 9999,
                      maxHeight: 200,
                    }),
                    menuList: (provided) => ({
                      ...provided,
                      maxHeight: 180,
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
                      color: state.isSelected ? 'white' : '#495057',
                      '&:hover': {
                        backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
                      },
                    }),
                  }}
                />
                {validationErrors.customer_Branch_Id && (
                  <div className="invalid-feedback d-block">
                    {validationErrors.customer_Branch_Id}
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup className="mb-3">
                <Label for="customer_CreditLimit">
                  Credit Limit
                </Label>
                <Input
                  type="number"
                  id="customer_CreditLimit"
                  name="customer_CreditLimit"
                  value={form.customer_CreditLimit}
                  onChange={handleInputChange}
                  placeholder="Enter credit limit"
                  className={validationErrors.customer_CreditLimit ? 'is-invalid' : ''}
                />
                {validationErrors.customer_CreditLimit && (
                  <div className="invalid-feedback d-block">
                    {validationErrors.customer_CreditLimit}
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>


          <Row>
            <Col md={12}>
              <FormGroup className="mb-3">
                <Label for="customer_Notes">Notes</Label>
                <Input
                  type="textarea"
                  id="customer_Notes"
                  name="customer_Notes"
                  value={form.customer_Notes}
                  onChange={handleInputChange}
                  placeholder="Enter additional notes"
                  rows={3}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup className="mb-3">
                <div className="form-check form-switch">
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id="customer_IsInternational"
                    name="customer_IsInternational"
                    checked={form.customer_IsInternational}
                    onChange={handleInputChange}
                  />
                  <Label className="form-check-label" htmlFor="customer_IsInternational">
                    International Customer
                  </Label>
                </div>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-3">
                <div className="form-check form-switch">
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id="customer_Active"
                    name="customer_Active"
                    checked={form.customer_Active}
                    onChange={handleInputChange}
                  />
                  <Label className="form-check-label" htmlFor="customer_Active">
                    {form.customer_Active ? "Active" : "Inactive"}
                  </Label>
                </div>
              </FormGroup>
            </Col>
          </Row>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? "Adding..." : "Add Customer"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default QuickAddCustomer;
